import { getValue, currencyFormat } from "components/Screens/CommonUtils";

import { s3ImagesBaseURL } from "util/APIBaseUrl";
import CUSTOMER_PAYROLL_STATUS from "constants/customer.constants";

export const EMPLOYEE_BREAKDOWN_MONTHLY_PAYMENT_COLUMNS = [
  {
    id: "employeeName",
    label: "Employee name",
    minWidth: 200,
    render: (value, row) => (
      <div className="employee-name-container">
        <img
          src={row.employeeImage || `${s3ImagesBaseURL}/placeholder-avatar.svg`}
          alt={value || "Employee"}
          className="employee-avatar-container"
          onError={(e) => {
            e.target.src = `${s3ImagesBaseURL}/placeholder-avatar.svg`;
          }}
        />
        <span className="para-body-m-medium color-text-heading-primary">{value}</span>
      </div>
    ),
  },
  {
    id: "currentSalary",
    label: "Charges",
    minWidth: 120,
    align: "right",
    render: (value) => (
      <span className="para-body-m-medium color-text-heading-primary">${currencyFormat(getValue(value)) || "-"}</span>
    ),
  },
  {
    id: "raise",
    label: "Raise",
    minWidth: 100,
    align: "right",
    render: (value) => (
      <span className="para-body-m-medium color-text-heading-primary">${currencyFormat(getValue(value)) || "-"}</span>
    ),
  },
  {
    id: "bonus",
    label: "Bonus",
    minWidth: 100,
    align: "right",
    render: (value) => (
      <span className="para-body-m-medium color-text-heading-primary">${currencyFormat(getValue(value)) || "-"}</span>
    ),
  },
  {
    id: "discount",
    label: "Discount",
    minWidth: 100,
    align: "right",
    render: (value) => (
      <span className="para-body-m-medium color-text-heading-primary">${currencyFormat(getValue(value)) || "-"}</span>
    ),
  },
  {
    id: "total",
    label: "Total",
    minWidth: 120,
    align: "right",
    render: (value) => (
      <span className="para-body-m-medium color-text-heading-primary">${currencyFormat(getValue(value)) || "-"}</span>
    ),
  },
];

export const ONE_TIME_PAYMENT_BREAKDOWN_COLUMNS = [
  {
    id: "description",
    label: "Description",
    minWidth: 240,
    render: (value) => <span className="para-body-m-medium color-text-heading-primary">{value || "-"}</span>,
  },
  {
    id: "implementationFee",
    label: "Implementation fee",
    minWidth: 180,
    align: "right",
    render: (value) => (
      <span className="para-body-m-medium color-text-heading-primary">${currencyFormat(getValue(value)) || "-"}</span>
    ),
  },
  {
    id: "fee",
    label: "Fee",
    minWidth: 120,
    align: "right",
    render: (value) => (
      <span className="para-body-m-medium color-text-heading-primary">${currencyFormat(getValue(value)) || "-"}</span>
    ),
  },
  {
    id: "discount",
    label: "Discount",
    minWidth: 100,
    align: "right",
    render: (value) => (
      <span className="para-body-m-medium color-text-heading-primary">
        ${currencyFormat(Math.abs(getValue(value))) || "-"}
      </span>
    ),
  },
  {
    id: "total",
    label: "Total",
    minWidth: 120,
    align: "right",
    render: (value) => (
      <span className="para-body-m-medium color-text-heading-primary">${currencyFormat(getValue(value)) || "-"}</span>
    ),
  },
];

export const getBackgroundImage = (payrollBreakdown) => {
  if (!payrollBreakdown) {
    return "var(--color-brand-primary-purple-100)";
  }

  if (payrollBreakdown.failure) {
    return "var(--color-primitives-danger-100)";
  }
  if ([CUSTOMER_PAYROLL_STATUS.PENDING, CUSTOMER_PAYROLL_STATUS.UNPAID].includes(payrollBreakdown.status)) {
    return "var(--color-primitives-warning-100)";
  }
  if (payrollBreakdown.status === CUSTOMER_PAYROLL_STATUS.VOID) {
    return "var(--color-primitives-neutral-100)";
  }
  return "var(--color-brand-primary-purple-100)";
};

export const getChipColor = (payrolBreakdown) => {
  if (payrolBreakdown?.failure) {
    return "error";
  }
  if (payrolBreakdown?.status === CUSTOMER_PAYROLL_STATUS.PENDING) {
    return "warning";
  }
  if (payrolBreakdown?.status === CUSTOMER_PAYROLL_STATUS.UNPAID) {
    return "warning";
  }
  if (payrolBreakdown?.status === CUSTOMER_PAYROLL_STATUS.VOID) {
    return "default";
  }
  return "success";
};

export const getChipLabel = (payrolBreakdown) => {
  if (payrolBreakdown?.failure) {
    return "Failed";
  }
  if (payrolBreakdown?.status === CUSTOMER_PAYROLL_STATUS.PENDING) {
    return "Processing";
  }
  if (payrolBreakdown?.status === CUSTOMER_PAYROLL_STATUS.UNPAID) {
    return "Draft";
  }
  if (payrolBreakdown?.status === CUSTOMER_PAYROLL_STATUS.VOID) {
    return "Void";
  }
  return "Paid";
};

export const getMonthlyPaymentOverviewDetails = (payrollBreakdown) => {
  if (!payrollBreakdown || Object.keys(payrollBreakdown).length === 0) {
    return [];
  }

  return [
    { label: "Billed on", value: payrollBreakdown?.billedOn },
    { label: "Due date", value: payrollBreakdown?.dueDate },
    { label: "Paid on", value: payrollBreakdown?.paidOn },
    { label: "Payment type", value: "Monthly" },
    { label: "Total employees", value: getValue(payrollBreakdown?.totalEmployees) },
    {
      label: "Credit adjusted",
      value: payrollBreakdown?.creditAdjustment
        ? `$${currencyFormat(Math.abs(getValue(payrollBreakdown?.creditAdjustment)))}`
        : "-",
    },
    {
      label: "Card surcharge",
      value: payrollBreakdown?.creditCardSurcharge
        ? `$${currencyFormat(Math.abs(getValue(payrollBreakdown?.creditCardSurcharge)))}`
        : "-",
    },
    {
      label: "Total bonus",
      value: payrollBreakdown?.totalBonus
        ? `$${currencyFormat(Math.abs(getValue(payrollBreakdown?.totalBonus)))}`
        : "-",
    },
    {
      label: "Total raise",
      value: payrollBreakdown?.totalRaise
        ? `$${currencyFormat(Math.abs(getValue(payrollBreakdown?.totalRaise)))}`
        : "-",
    },
  ];
};

export const getOneTimePaymentOverviewDetails = (payrollBreakdown) => {
  if (!payrollBreakdown || Object.keys(payrollBreakdown).length === 0) {
    return [];
  }

  return [
    { label: "Billed on", value: payrollBreakdown?.billedOn },
    { label: "Due date", value: payrollBreakdown?.dueDate },
    { label: "Paid on", value: payrollBreakdown?.paidOn },
    { label: "Payment type", value: "One-time" },
    {
      label: "Implementation fee",
      value: payrollBreakdown?.subtotal?.totalImplementationFees
        ? `$${currencyFormat(getValue(payrollBreakdown?.subtotal?.totalImplementationFees))}`
        : "-",
    },
    {
      label: "Fee",
      value: payrollBreakdown?.subtotal?.totalFees
        ? `$${currencyFormat(getValue(payrollBreakdown?.subtotal?.totalFees))}`
        : "-",
    },
    {
      label: "Discount",
      value: payrollBreakdown?.subtotal?.totalDiscounts
        ? `$${currencyFormat(Math.abs(getValue(payrollBreakdown?.subtotal?.totalDiscounts)))}`
        : "-",
    },
  ];
};

export const InvoiceStatusMap = {
  paid: "paid",
  Successful: "paid",
  Processing: "pending",
  Failed: "failed",
  Unpaid: "un-paid",
};
